import React, { useState } from "react";
import uuid from 'react-uuid';
import '../css/ActionRequestForm.css';
import { withFormCache } from "../FormCacheResources";
import { onRequestFailure } from "./AuthHandler";

// Define initial state for all form fields to be cached
const initialState = {
  type: "PUT",
  username: "",
  title: "",
  reuseHandle: false,
  inputHandle: "",
  body: "",
  action1: "",
  action2: "",
  sound: "default",
  snoozeEnabled: "",
  omitSnooze: false,
  hideInMotion: "",
  buttonSize: "",
  snoozeDuration: 15,
  renderDuration: 15,
  omitRenderDuration: false,
  nullRenderDuration: false,
  ttsAnnouncement: "",
  ttsReplayLimit: 3,
  omitReplayLimit: true,
  nullReplayLimit: false,
  ttsAutoplayEnabled: ""
};

const ActionRequestForm = ({
  token,
  clientKey,
  onResponse,
  formState,
  setType,
  setUsername,
  setTitle,
  setReuseHandle,
  setInputHandle,
  setBody,
  setAction1,
  setAction2,
  setSound,
  setSnoozeEnabled,
  setSnoozeDuration,
  setRenderDuration,
  setOmitRenderDuration,
  setNullRenderDuration,
  setOmitSnooze,
  setHideInMotion,
  setButtonSize,
  setTtsAnnouncement,
  setTtsReplayLimit,
  setOmitReplayLimit,
  setNullReplayLimit,
  setTtsAutoplayEnabled,
  clearForm
}) => {  
  const [errors, setErrors] = useState(null);

  const createRequestBody = () => {
    const actions = createActionsObject();
    const reqBody = {};
    const snoozeObj = {};

    if (formState.snoozeEnabled === "" || formState.snoozeEnabled === null) {
      snoozeObj.enabled = null;
    } else {
      snoozeObj.enabled = formState.snoozeEnabled === "true";
    }

    snoozeObj.snooze_duration_minutes = formState.snoozeDuration;

    if (!formState.omitReplayLimit) {
      reqBody.text_to_speech_replay_limit = formState.nullReplayLimit ? null : formState.ttsReplayLimit;
    }

    if (formState.ttsAutoplayEnabled !== "") {
      reqBody.text_to_speech_autoplay_enabled =
        formState.ttsAutoplayEnabled === "null"
          ? null
          : formState.ttsAutoplayEnabled === "true";
    }

    if (formState.hideInMotion === "null") {
      reqBody.hide_while_in_motion = null;
    } else if (formState.hideInMotion != null && formState.hideInMotion != "") {
      reqBody.hide_while_in_motion = formState.hideInMotion === "true"
    }

    if (formState.buttonSize !== "") {
      reqBody.action_button_size = formState.buttonSize === "null" ? null : formState.buttonSize;
    }

    reqBody.title = formState.title || null;
    reqBody.body = formState.body || null;
    reqBody.actions = actions;
    reqBody.sound = formState.sound === "none" ? null : formState.sound;
    reqBody.text_to_speech_announcement = formState.ttsAnnouncement || null;

    if (!formState.omitRenderDuration) {
      reqBody.render_duration = formState.nullRenderDuration ? null : formState.renderDuration;
    }

    if (!formState.omitSnooze) {
      reqBody.snooze = snoozeObj;
    }

    return reqBody;
  };

  const createResponseMessage = (message, handle) => {
    const date = new Date();
    const time = `[${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}] - `;
    return `${time}${message} (Handle: ${handle})`;
  };

  const parseActionInput = (input, fieldId) => {
    if (input !== "") {
      try {
        const escaped = input.replace(/(\r\n|\n|\r)/gm, "");
        return JSON.parse(escaped);
      } catch {
        const newError = `Error parsing action ${fieldId}, invalid JSON`;
        setErrors(newError);
        return null;
      }
    }
    return null;
  };

  const createActionsObject = () => {
    let actionsObject = [];
    setErrors(null);

    if (formState.action1 !== "") {
      const parsed = parseActionInput(formState.action1, "1");
      if (parsed) actionsObject.push(parsed);
    }
    if (formState.action2 !== "") {
      const parsed = parseActionInput(formState.action2, "2");
      if (parsed) actionsObject.push(parsed);
    }

    return actionsObject;
  };

  const sendActionRequest = () => {
    const baseUrl = "https://squid-fortress-staging.eleostech.com/api";

    const requestBody = createRequestBody();
    if (requestBody.actions.some(action => action === null)) return;

    const requestOptions = {
        method: formState.type,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Eleos-Admin-Client-Key": clientKey || "",
        },
      };

    if (formState.type === "PUT") {
      requestOptions.body = JSON.stringify(requestBody);
    }

    if (!token || !clientKey) {
      alert("Token and Client Key are required in staging.");
      return;
    }

    let handle = formState.reuseHandle && formState.inputHandle ? formState.inputHandle : uuid();
    if (!formState.reuseHandle) setInputHandle(handle);

    const requestUrl = `${baseUrl}/v1/action_requests/${formState.username}/${handle}`;
    onResponse(`Request Sent to ${requestUrl}`);

    fetch(requestUrl, requestOptions)
      .then((resp) => {
        if (resp.status === 403 || resp.status === 401) {
          onRequestFailure();
          throw new Error(`${resp.status} ${resp.statusText}`);
        }
        return resp.text().then((response) => {
          const message = `Response Status: ${resp.status}, Response Body: ${response}`;
          onResponse(createResponseMessage(message, handle));
        });
      })
      .catch((error) => {
        const message = `Response: Error - ${error.message}`;
        onResponse(createResponseMessage(message, handle));
      });
  };

  const handleActionRequest = (event) => {
    event.preventDefault();
    if (!formState.username.trim()) {
      alert("Please enter a username.");
      return;
    }
    sendActionRequest();
  };

  return (
    <div className="action-request-form">
      <h3>Action Request</h3>
      <form className="actionRequest-form-container" onSubmit={handleActionRequest}>
        <label className="actionRequest-form-label">
          Type:
          <select value={formState.type} name="type" onChange={(e) => setType(e.target.value)} className="actionRequest-select-input">
            <option value="PUT">Create</option>
            <option value="DELETE">Delete</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
          Username:
          <input
            name="username"
            type="text"
            value={formState.username}
            onChange={(e) => setUsername(e.target.value)}
            className="actionRequest-text-input"
            placeholder="Enter username"
          />
        </label>
        <label className="actionRequest-form-label">
          Handle:
          <input
            name="handle"
            type="text"
            value={formState.inputHandle}
            onChange={(e) => setInputHandle(e.target.value)}
            className="actionRequest-text-input"
            placeholder="Enter handle or leave blank for new UUID"
          />
        </label>
        <label className="actionRequest-form-label actionRequest-checkbox-label">
          Preserve Handle:
          <input
            name="reuseHandle"
            type="checkbox"
            checked={formState.reuseHandle}
            onChange={(e) => setReuseHandle(e.target.checked)}
            className="actionRequest-checkbox-input"
          />
        </label>
        <label className="actionRequest-form-label">
          Title:
          <input
            disabled={formState.type === "DELETE"}
            name="title"
            type="text"
            value={formState.title}
            onChange={(e) => setTitle(e.target.value)}
            className="actionRequest-text-input"
            placeholder="Enter title"
          />
        </label>
        <label className="actionRequest-form-label">
          Body:
          <input
            disabled={formState.type === "DELETE"}
            name="body"
            type="text"
            value={formState.body}
            onChange={(e) => setBody(e.target.value)}
            className="actionRequest-text-input"
            placeholder="Enter body"
          />
        </label>
        <label className="actionRequest-form-label">
          TTS Announcement:
          <textarea
            disabled={formState.type === "DELETE"}
            cols={50}
            rows={3}
            name="ttsAnnouncement"
            type="text"
            value={formState.ttsAnnouncement}
            onChange={(e) => setTtsAnnouncement(e.target.value)}
            className="actionRequest-text-input"
            placeholder="Enter TTS Announcement"
          />
        </label>
        <label className="actionRequest-form-label">
          TTS Replay Limit:
          <input
            disabled={formState.type === "DELETE" || formState.omitReplayLimit}
            type="number"
            value={formState.ttsReplayLimit}
            name="ttsReplayLimit"
            onChange={(e) => setTtsReplayLimit(parseInt(e.target.value))}
            className="actionRequest-select-input"
          />
        </label>
        <div className="actionRequest-checkbox-group" >
          <label className="actionRequest-form-label actionRequest-checkbox-label">
            Omit Replay Limit:
            <input
              disabled={formState.type === "DELETE"}
              name="omitReplayLimit"
              type="checkbox"
              checked={formState.omitReplayLimit}
              onChange={(e) => setOmitReplayLimit(e.target.checked)}
              className="actionRequest-checkbox-input"
            />
          </label>  
          <label className="actionRequest-form-label actionRequest-checkbox-label">
            Null Replay Limit:
            <input
              disabled={formState.type === "DELETE"}
              name="nullReplayLimit"
              type="checkbox"
              checked={formState.nullReplayLimit}
              onChange={(e) => setNullReplayLimit(e.target.checked)}
              className="actionRequest-checkbox-input"
            />
          </label>
        </div>
        <label className="actionRequest-form-label">
          TTS Autoplay Enabled:
          <select
            disabled={formState.type === "DELETE"}
            value={formState.ttsAutoplayEnabled}
            name="ttsAutoplayEnabled"
            onChange={(e) => setTtsAutoplayEnabled(e.target.value)}
            className="actionRequest-select-input"
          >
            <option value={""}></option>
            <option value="true">True</option>
            <option value="false">False</option>
            <option value="null">Null</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
          Sound:
          <select
            disabled={formState.type === "DELETE"}
            value={formState.sound}
            name="sound"
            onChange={(e) => setSound(e.target.value)}
            className="actionRequest-select-input"
          >
            <option value="none">None</option>
            <option value="default">Default</option>
            <option value="honk">Honk</option>
            <option value="guitar">Guitar</option>
            <option value="echo">Echo</option>
            <option value="marimba">Marimba</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
          Hide while in motion:
          <select
            disabled={formState.type === "DELETE"}
            value={formState.hideInMotion}
            name="hide_while_in_motion"
            onChange={(e) => setHideInMotion(e.target.value)}
            className="actionRequest-select-input"
          >
            <option value={""}></option>
            <option value="true">True</option>
            <option value="false">False</option>
            <option value="null">Null</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
          Button size:
          <select
            disabled={formState.type === "DELETE"}
            value={formState.buttonSize}
            name="action_button_size"
            onChange={(e) => setButtonSize(e.target.value)}
            className="actionRequest-select-input"
          >
            <option value={""}></option>
            <option value="default">Default</option>
            <option value="large">Large</option>
            <option value="null">Null</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
          Render Duration Minutes:
          <input
            disabled={formState.type === "DELETE" || formState.omitRenderDuration}
            type="number"
            name="renderDuration"
            value={formState.renderDuration}
            onChange={(e) => setRenderDuration(parseInt(e.target.value))}
            className="actionRequest-text-input"
          />
        </label>
        <div className="actionRequest-checkbox-group" >
          <label className="actionRequest-form-label actionRequest-checkbox-label">
            Omit Render Duration:
            <input
              disabled={formState.type === "DELETE"}
              name="omitRenderDuration"
              type="checkbox"
              checked={formState.omitRenderDuration}
              onChange={(e) => setOmitRenderDuration(e.target.checked)}
              className="actionRequest-checkbox-input"
            />
          </label>  
          <label className="actionRequest-form-label actionRequest-checkbox-label">
            Null Render Duration:
            <input
              disabled={formState.type === "DELETE"}
              name="nullRenderDuration"
              type="checkbox"
              checked={formState.nullRenderDuration}
              onChange={(e) => setNullRenderDuration(e.target.checked)}
              className="actionRequest-checkbox-input"
            />
          </label>
        </div>
        <label className="actionRequest-form-label">
          Snooze Enabled:
          <select
            disabled={formState.type === "DELETE"}
            value={formState.snoozeEnabled}
            name="snoozeEnabled"
            onChange={(e) => setSnoozeEnabled(e.target.value)}
            className="actionRequest-select-input"
          >
            <option value="">-- Select --</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        </label>
        <label className="actionRequest-form-label">
            Snooze Duration Minutes:
            <input
              disabled={formState.type === "DELETE" || formState.omitSnooze}
              type="number"
              name="snoozeDuration"
              value={formState.snoozeDuration}
              onChange={(e) => setSnoozeDuration(parseInt(e.target.value))}
              className="actionRequest-text-input"
            />
        </label>    
        <label className="actionRequest-form-label actionRequest-checkbox-label">
          Omit Snooze Object:
          <input
            disabled={formState.type === "DELETE"}
            name="omitSnooze"
            type="checkbox"
            checked={formState.omitSnooze}
            onChange={(e) => setOmitSnooze(e.target.checked)}
            className="actionRequest-checkbox-input"
          />
        </label>
        {errors && <div className="actionRequest-error-message">{errors}</div>}
        <label className="actionRequest-form-label">
          <span>Action 1:</span>
          <textarea
            disabled={formState.type === "DELETE"}
            cols={50}
            rows={3}
            name="action1"
            value={formState.action1}
            onChange={(e) => setAction1(e.target.value)}
            className="actionRequest-textarea-input"
            placeholder="Enter JSON for Action 1"
          />
        </label>
        <label className="actionRequest-form-label">
          <span>Action 2:</span>
          <textarea
            disabled={formState.type === "DELETE"}
            cols={50}
            rows={3}
            name="action2"
            value={formState.action2}
            onChange={(e) => setAction2(e.target.value)}
            className="actionRequest-textarea-input"
            placeholder="Enter JSON for Action 2"
          />
        </label>
        <div className="actionRequest-button-group">
          <button type="submit" className="actionRequest-send-button">SEND</button>
          <button type="button" className="actionRequest-clear-button" onClick={() => clearForm()}>CLEAR</button>
        </div>
      </form>
    </div>
  );
};

export default withFormCache(ActionRequestForm, 'actionRequestForm', initialState);